import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PageLayout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Heart, ArrowRight, Spinner } from '@phosphor-icons/react';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(formData.email, formData.password);
      toast.success(`Welcome back, ${user.name}!`);
      navigate(user.is_admin ? '/admin' : '/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout showFooter={false}>
      <div className="min-h-screen flex items-center justify-center rose-gradient p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-10">
            <Link to="/" className="inline-flex flex-col items-center gap-2 mb-8">
              <div className="flex items-center gap-2">
                <Heart weight="fill" className="w-10 h-10 text-primary" />
                <Heart weight="fill" className="w-6 h-6 text-amber-400" />
              </div>
              <span className="script-font text-3xl text-primary">Link, Laugh & Love</span>
            </Link>
            <h1 className="font-serif text-3xl text-gray-800 mb-2">Welcome Back</h1>
            <p className="text-muted-foreground">
              Sign in to check your matches and continue your journey
            </p>
          </div>

          <div className="resume-card rounded-lg p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="gold-accent"></div>
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
                Sign In
              </span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-1">
                <Label htmlFor="email" className="form-label">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="login-email-input"
                  className="form-input"
                />
              </div>

              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="form-label">Password</Label>
                  <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                    Forgot Password?
                  </Link>
                </div>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="login-password-input"
                  className="form-input"
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="btn-primary w-full py-6"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <Spinner className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Heart weight="fill" className="w-4 h-4 mr-2" />
                    Sign In
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </div>

          <p className="text-center text-muted-foreground mt-8">
            Don't have an account?{' '}
            <Link to="/signup" className="text-primary font-medium hover:underline">
              Start Your Journey
            </Link>
          </p>
        </div>
      </div>
    </PageLayout>
  );
};

export default Login;
